"use client"

/**
 * Skip-Ads reward modal — click the floating dollar-sign button (see
 * SkipAdsButton.tsx), watch one unskippable video ad, get a real 30-minute
 * ad-free window afterward. The 4 sitewide corner widgets (currently
 * disabled — see lib/skip-ads.ts) are also forced on for this one moment,
 * so what's on screen genuinely is "here are the ads, all at once, then
 * you're done" rather than one video standing in for the whole thing.
 *
 * Full-screen and isolated on purpose (not a small popup layered over
 * wherever the user was): leaving mid-video would forfeit the reward
 * anyway, and a dedicated screen means an accidental click elsewhere can't
 * reset whatever the user was doing on the actual page underneath.
 *
 * Backdrop sits at z-30, BELOW the corner widgets' z-40 — they're fixed to
 * the real screen corners regardless of what's mounted where, and need to
 * render visibly on top of the dim backdrop, not be hidden behind it. The
 * video itself renders inline (confined to a centered box, not
 * full-screen-takeover) so the corner ads stay visible around it instead of
 * one opaque z-[200] layer covering the whole screen.
 */

import { useEffect, useState } from "react"
import { createPortal } from "react-dom"
import { X } from "@/lib/icons"
import { VideoPreroll } from "@/components/ads/VideoPreroll"
import { useAdTriggerStore } from "@/lib/ad-trigger-store"
import { setSkipAdsModalActive, grantSkipAds } from "@/lib/skip-ads"
import { useTranslations } from "next-intl"

interface SkipAdsModalProps {
  onClose: () => void
  /** Called once the reward is actually earned (video watched through) —
   * separate from onClose, which also fires on early cancel. */
  onGranted: () => void
  /** Admin-controlled (/api/admin/skip-ads-config), passed down from
   * SkipAdsButton rather than fetched again here. Defaults to 30 to match
   * the config's own DB default, for any caller that doesn't pass it. */
  grantMinutes?: number
}

interface VideoZone {
  vastUrl: string | null
  zoneId: string | null
}

export function SkipAdsModal({ onClose, onGranted, grantMinutes = 30 }: SkipAdsModalProps) {
  const t = useTranslations("skipAdsModal")
  const [videoZone, setVideoZone] = useState<VideoZone | null | undefined>(undefined)
  const [done, setDone] = useState(false)
  const { triggerStickyBanner, triggerVideoSlider, triggerInPagePush, triggerInstantMessage } = useAdTriggerStore()

  useEffect(() => {
    setSkipAdsModalActive(true)
    // Order matters: the modal-active flag must be set BEFORE these fire, so
    // the corner widgets' own fetch effects (which run in reaction to the
    // trigger-count bump below) see it already true and don't bail out on
    // their normal sitewide enabled:false check.
    triggerStickyBanner()
    triggerVideoSlider()
    triggerInPagePush()
    triggerInstantMessage()

    fetch("/api/ads/network-grid-ad?type=video&tier=3")
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setVideoZone(d?.vast_url ? { vastUrl: d.vast_url, zoneId: d.zone_id ?? null } : null))
      .catch(() => setVideoZone(null))

    return () => setSkipAdsModalActive(false)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleVideoComplete = () => {
    setDone(true)
    grantSkipAds(grantMinutes)
    onGranted()
  }

  if (typeof document === "undefined") return null

  return createPortal(
    <div className="fixed inset-0 z-30 bg-black/90 backdrop-blur-sm flex flex-col items-center justify-center p-4">
      {/* Part of the centered flex column (not absolutely pinned to the top
          edge) so it can't land in the same screen-corner real estate as the
          4 corner widgets forced on above (In-Page Push top-right, Sticky
          Banner top-left) — those render at z-40, well above where this used
          to sit. */}
      <div className="mb-4 text-white text-sm bg-black/60 px-4 py-2 rounded-full text-center">
        {done
          ? `Ad-free for the next ${grantMinutes} minutes — enjoy.`
          : `Watch this ad through to unlock ${grantMinutes} minutes ad-free`}
      </div>

      {/* z-50, above the corner widgets' z-40 — the In-Page Push card shares
          this same top-right corner and was rendering on top of this button,
          silently blocking clicks on it. */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-50 h-9 w-9 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
        aria-label={done ? t("close") : t("cancelNoReward")}
      >
        <X className="h-4 w-4" />
      </button>

      <div className="relative w-full max-w-3xl aspect-video">
        {videoZone === undefined ? (
          <div className="absolute inset-0 flex items-center justify-center text-white/60 text-sm animate-pulse">
            {t("loadingAd")}
          </div>
        ) : videoZone === null ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-white/70 text-sm">
            {t("noAdAvailableRightNowTryAgainInABit")}
          </div>
        ) : done ? (
          <div className="absolute inset-0 flex items-center justify-center text-white text-lg font-semibold">
            {t("enjoyYourAdFreeMinutes", { minutes: grantMinutes })}
          </div>
        ) : (
          <VideoPreroll
            inline
            vastUrl={videoZone.vastUrl ?? undefined}
            zoneId={videoZone.zoneId}
            skipAfterSecs={0}
            onComplete={handleVideoComplete}
          />
        )}
      </div>
    </div>,
    document.body
  )
}
